import React from 'react';
import { useResultList } from './hooks/useResultList';
import { Link, useParams } from 'react-router-dom';
import LoadingSpiner from '../Loader/LoadingSpiner';
import { FaExclamationCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { selectResultIds, selectResultIdsState } from '../../Store/resultSlice';
import { obfuscateImageName } from '../../utils/sizeToText';
import convertS3UrlToHttps from '../../utils/convertS3UrlToHttps';
import placeholderImage from './assets/empty-result-placeholder.png';

interface ResultSidebarProps {
    imageId: string;
}

export interface Result {
    id: string;
    name: string;
    created_at: string;
    status: string;
    result_data: Array<{
        'AI-Measurement'?: {
            withBackground?: any;
            withoutBackground?: any;
        };
        Images?: {
            Overlay: string;
            Original: string;
            original: string;
            overlay: string;
            thumbnail?: string;
        };
        label: string;
    }>;
}

export default function ResultSidebar(props: ResultSidebarProps) {
    const { imageId } = props;
    const { projectId } = useParams();
    const { data: results, isLoading, isError } = useResultList(imageId);
    console.log("ResultSidebar", results)
    const selectedResultIds = useSelector(selectResultIdsState);
    const dispatch = useDispatch();

    const handleResultSelect = (resultId: string, coreIndex?: number) => {
        const currentIndex = selectedResultIds.indexOf(resultId);
        const newSelectedResults = Array.from(selectedResultIds);

        if (currentIndex === -1) {
            newSelectedResults.push(resultId);
        } else {
            newSelectedResults.splice(currentIndex, 1);
        }

        dispatch(selectResultIds(newSelectedResults));
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked && validResults) {
            const newSelectedResults = validResults.map((result: Result) => result.id);
            dispatch(selectResultIds(newSelectedResults));
            return;
        }
        dispatch(selectResultIds([]))
    };

    // Separate valid and skipped results
    const validResults = results?.filter((result: Result) => result.status !== "Skipped" && result.result_data) || [];
    console.log("validResults", validResults);
    const skippedResults = results?.filter((result: Result) => result.status === "Skipped") || [];

    const isAllSelected = validResults?.length === selectedResultIds.length;

    if (isLoading) return <div className='text-center mt-6'><LoadingSpiner/></div>;
    if (isError) return <div><p className='text-center mt-6'><FaExclamationCircle className='inline mr-2'/> Error loading results</p></div>;
    if (!results || results.length === 0) return <div className='text-center mt-6'>No results available</div>;

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const renderResultItem = (result: Result) => {
        const obfuscatedName = obfuscateImageName(result.name);
        const type = result.name.toLowerCase().includes('quality') ? 'Quality' : 'Segmentation';
        const displayName = `${obfuscatedName} - ${type}`;
        // If there's only one result_data item, display as before
        if (!result.result_data || result.result_data.length <= 1) {
            return (
                <div key={result.id} className="flex items-center p-1 hover:bg-gray-100 rounded">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        onChange={() => handleResultSelect(result.id)}
                        checked={selectedResultIds.includes(result.id)}
                    />
                    <Link 
                        to={`/projects/${projectId}/images/${imageId}/results/${result.id}`}
                        className="ml-2 flex-1 min-w-0"
                    >
                        <div className="text-sm font-medium truncate">{displayName}</div>
                        <div className="text-xs text-gray-500">{formatDate(result.created_at)}</div>
                    </Link>
                </div>
            );
        }

        // For TMA results (multiple result_data items), show each core without checkboxes
        return result.result_data.map((data, index) => {
            // Skip quality results for TMA
            if (data['AI-Measurement']?.withoutBackground?.GoodPercentage !== undefined) {
                return null;
            }

            const isSelected = selectedResultIds.includes(result.id);
            const isCoreSelected = isSelected && selectedResultIds[selectedResultIds.indexOf(result.id)] === `${result.id}-${index}`;

            return (
                <div key={`${result.id}-${index}`} className="flex items-center p-1 hover:bg-gray-100 rounded">
                    {/* Hide checkbox for TMA results */}
                    <Link 
                        to={`/projects/${projectId}/images/${imageId}/results/${result.id}?dataIndex=${index}`}
                        className="ml-2 flex-1 min-w-0 flex items-center"
                    >
                        {data.Images?.thumbnail && (
                            <img 
                                src={convertS3UrlToHttps(data.Images.thumbnail)}
                                alt={`Core ${data.label}`}
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholderImage; }}
                                className="w-12 h-12 object-cover rounded mr-2"
                            />
                        )}
                        <div>
                            <div className="text-sm font-medium truncate">
                                Core {data.label}
                            </div>
                            <div className="text-xs text-gray-500">{formatDate(result.created_at)}</div>
                            {data['AI-Measurement'] && (
                                <div className="text-xs text-gray-400 mt-1">
                                    Tissue Segmentation
                                </div>
                            )}
                        </div>
                    </Link>
                </div>
            );
        }).filter(Boolean); // Remove null entries (quality results)
    };

    return (
        <div className="h-full flex flex-col">
            <div className="p-2 flex-shrink-0">
                {/* Hide 'Select All' checkbox for TMA results */}
                {!validResults.some((result: Result) => result.result_data.length > 1) && (
                    <div className="flex items-center mb-2">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-sm"
                            onChange={handleSelectAllClick}
                            checked={isAllSelected}
                        />
                        <span className="ml-2 text-sm font-semibold">Select All</span>
                    </div>
                )}
            </div>
            <div className="flex-1 overflow-y-auto">
                <div className="p-2 space-y-1">
                    {validResults?.map((result: Result) => renderResultItem(result))}
                </div>
                {skippedResults.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 p-2">
                        <div className="text-sm text-gray-500 mb-2">Skipped Results</div>
                        <div className="space-y-1">
                            {skippedResults?.map((result: Result) => (
                                <div key={result.id} className="flex items-center p-1 rounded opacity-50">
                                    <div className="ml-6 flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate text-gray-500">{result.name}</div>
                                        <div className="text-xs text-gray-400 flex items-center gap-2">
                                            <span>{formatDate(result.created_at)}</span>
                                            <span>•</span>
                                            <span className="text-gray-400">{result.status}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
} 